import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import { socketService } from "@/Services/socketService";

import { updateNetworkingStatus as updateNetworkingStatusApi } from "@/Services/EventMeetingServices";
import { postMeetingNotes, getMeetingNotes } from "@/Services/EventMeetingServices";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL?.trim();

/* ----------------------------- small helpers ----------------------------- */

function makeId() {
  return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

async function fetchJson(url, { method = "GET", token, body } = {}) {
  const res = await fetch(url, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  const text = await res.text();
  let json = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }

  if (!res.ok) {
    const msg =
      json?.message || json?.error || `Request failed (${res.status})`;
    throw new Error(msg);
  }
  return json;
}

/* ----------------------------- UI: overlays ----------------------------- */

function FloatingEmojiOverlay({ items }) {
  if (!items?.length) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        pointerEvents: "none",
        zIndex: 9999,
      }}
    >
      {items.map((it) => (
        <div
          key={it.id}
          style={{
            position: "absolute",
            left: `${it.leftPct}%`,
            bottom: "10%",
            animation: "mrf_floatUp 2.6s ease-out forwards",
            filter: "drop-shadow(0 8px 18px rgba(0,0,0,0.35))",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 6,
            }}
          >
            <div style={{ fontSize: "38px", lineHeight: 1 }}>{it.emoji}</div>
            {!!it.fromName && (
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#fff",
                  background: "rgba(0,0,0,0.45)",
                  border: "1px solid rgba(255,255,255,0.18)",
                  padding: "3px 8px",
                  borderRadius: 999,
                  maxWidth: 140,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {it.fromName}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function LikeOverlay({ bursts }) {
  if (!bursts?.length) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        pointerEvents: "none",
        zIndex: 9999,
      }}
    >
      {bursts.map((b) => (
        <div
          key={b.id}
          style={{
            position: "absolute",
            left: `${b.leftPct}%`,
            bottom: `${b.bottomPct}%`,
            animation: "mrf_heartPulse 2s ease-out forwards",
            filter: "drop-shadow(0 8px 18px rgba(255,50,50,0.5))",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 6,
          }}
        >
          <div style={{ fontSize: "52px", lineHeight: 1 }}>❤️</div>
          {b.fromName && (
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: "#fff",
                background: "rgba(0,0,0,0.55)",
                border: "1px solid rgba(255,100,100,0.4)",
                padding: "3px 10px",
                borderRadius: 999,
                maxWidth: 160,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {b.fromName} liked you!
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/* ----------------------------- UI: emoji bar ----------------------------- */

function EmojiQuickBar({ onSend }) {
  const scrollRef = React.useRef(null);

  const EMOJI_LIST = [
    { emoji: "👍", label: "Like" },
    { emoji: "❤️", label: "Love" },
    { emoji: "😂", label: "Haha" },
    { emoji: "🎉", label: "Party" },
    { emoji: "👏", label: "Clap" },
    { emoji: "🔥", label: "Fire" },
    { emoji: "😮", label: "Wow" },
    { emoji: "🙌", label: "Hands" },
    { emoji: "💡", label: "Idea" },
    { emoji: "✅", label: "Check" },
    { emoji: "❓", label: "Question" },
    { emoji: "⚡", label: "Energy" },
    { emoji: "🫶", label: "Heart" },
    { emoji: "😎", label: "Cool" },
    { emoji: "🤝", label: "Deal" },
    { emoji: "🎤", label: "Speaker" },
    { emoji: "📢", label: "Announcement" },
    { emoji: "📣", label: "Shoutout" },
    { emoji: "🎧", label: "Listening" },
    { emoji: "📸", label: "Photo" },
    { emoji: "🎬", label: "Live" },
    { emoji: "⌛", label: "Waiting" },
    { emoji: "⏰", label: "Reminder" },
    { emoji: "🚀", label: "Launch" },
    { emoji: "🏆", label: "Winner" },
    { emoji: "🥳", label: "Celebrate" },
    { emoji: "🎯", label: "Target" },
    { emoji: "🌟", label: "Star" },
    { emoji: "💯", label: "Perfect" },
    { emoji: "📈", label: "Growth" },
    { emoji: "🧠", label: "Smart" },
    { emoji: "✍️", label: "Note" },
    { emoji: "🗣️", label: "Discussion" },
    { emoji: "👀", label: "Watching" },
    { emoji: "🙏", label: "Thanks" },
    { emoji: "🤩", label: "Excited" },
    { emoji: "😴", label: "Boring" },
    { emoji: "☕", label: "Break" },
    { emoji: "📍", label: "Location" },
    { emoji: "🌍", label: "Global" },
    { emoji: "🎶", label: "Music" },
    { emoji: "🛎️", label: "Alert" },
    { emoji: "📚", label: "Learning" },
    { emoji: "🧩", label: "Problem Solved" },
    { emoji: "🎁", label: "Gift" },
  ];

  const scroll = (direction) => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({
      left: direction === "left" ? -260 : 260,
      behavior: "smooth",
    });
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        width: "100%",
        maxWidth: 620,
      }}
    >
      <button type="button" onClick={() => scroll("left")} style={arrowBtn}>
        <i className="fa fa-chevron-left"></i>
      </button>

      <div
        ref={scrollRef}
        className="emoji-scrollbar-hide"
        style={{
          display: "flex",
          gap: 8,
          overflowX: "auto",
          scrollBehavior: "smooth",
          width: "100%",
          padding: "4px 2px",
        }}
      >
        {EMOJI_LIST.map((item) => (
          <button
            key={item.emoji}
            type="button"
            onClick={() => onSend?.(item.emoji)}
            title={item.label}
            style={{
              minWidth: 42,
              height: 42,
              borderRadius: 12,
              border: "1px solid rgba(255,255,255,0.18)",
              background: "rgba(0,0,0,0.25)",
              color: "#fff",
              cursor: "pointer",
              fontSize: 20,
              flexShrink: 0,
              transition: "all 0.18s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "scale(1.08)";
              e.currentTarget.style.background = "rgba(255,255,255,0.12)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.style.background = "rgba(0,0,0,0.25)";
            }}
          >
            {item.emoji}
          </button>
        ))}
      </div>

      <button type="button" onClick={() => scroll("right")} style={arrowBtn}>
        <i className="fa fa-chevron-right"></i>
      </button>

      <style>
        {`
          .emoji-scrollbar-hide::-webkit-scrollbar { display: none; }
          .emoji-scrollbar-hide {
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
        `}
      </style>
    </div>
  );
}

const arrowBtn = {
  width: 36,
  height: 36,
  borderRadius: 10,
  border: "1px solid rgba(255,255,255,0.2)",
  background: "rgba(0,0,0,0.35)",
  color: "#fff",
  cursor: "pointer",
  flexShrink: 0,
  fontSize: 16,
};

/* ----------------------------- Notes Modal ----------------------------- */

function NotesModal({ open, onClose, token, sessionId, eventId, tableId }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState([]);
  const [note, setNote] = useState("");

  // const notesUrl = useMemo(() => {
  //   if (!API_BASE_URL || !sessionId) return null;
  //   return `${API_BASE_URL}/sessions/${sessionId}/notes`;
  // }, [sessionId]);

  const load = useCallback(async () => {
  if (!sessionId) return;
  setLoading(true);
  try {
    const res = await getMeetingNotes(sessionId);
    setNotes(res?.data || []);
  } catch (e) {
    toast.error(e.message || "Failed to load notes");
  } finally {
    setLoading(false);
  }
}, [sessionId]);

  useEffect(() => {
    if (!open) return;
    load();
  }, [open, load]);

  const save = useCallback(async () => {
  if (!sessionId) return;
  if (!eventId) return toast.error("Missing eventId");
  const trimmed = note.trim();
  if (!trimmed) return;

  setSaving(true);
  try {
    const payload = {
      eventId,
      ...(tableId ? { tableId } : {}),
      note: trimmed,
    };

    const res = await postMeetingNotes(sessionId, payload);

    const created = res?.data || res;
    if (created?._id) {
      setNotes((prev) => [created, ...prev]);
      setNote("");
      toast.success("Note saved");
    } else {
      toast.success("Saved");
      load();
    }
  } catch (e) {
    toast.error(e.message || "Failed to save note");
  } finally {
    setSaving(false);
  }
}, [sessionId, eventId, tableId, note, load]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onMouseDown={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        zIndex: 9999,
        display: "grid",
        placeItems: "center",
        padding: 14,
      }}
    >
      <div onMouseDown={(e) => e.stopPropagation()} className="session-note">
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div style={{ fontWeight: 700 }}>Session Notes</div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              color: "#fff",
              cursor: "pointer",
              fontSize: 18,
            }}
          >
            ✕
          </button>
        </div>

        <div style={{ marginTop: 12 }}>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Write a note..."
            rows={3}
            style={{
              width: "100%",
              borderRadius: 12,
              padding: 10,
              border: "1px solid rgba(255,255,255,0.12)",
              background: "rgba(255,255,255,0.06)",
              color: "#fff",
              outline: "none",
            }}
          />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button
              type="button"
              onClick={save}
              disabled={saving || !note.trim()}
              style={{
                marginTop: 10,
                padding: "10px 12px",
                borderRadius: 12,
                border: "1px solid rgba(255,255,255,0.12)",
                background: saving ? "rgba(255,255,255,0.08)" : "#22c55e",
                color: "#06230f",
                cursor: saving ? "not-allowed" : "pointer",
                fontWeight: 700,
              }}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>

        <div style={{ marginTop: 14 }}>
          {loading ? (
            <div style={{ opacity: 0.85 }}>Loading...</div>
          ) : notes.length === 0 ? (
            <div style={{ opacity: 0.75 }}>No notes yet.</div>
          ) : (
            <div style={{ display: "grid", gap: 10 }}>
              {notes.map((n) => (
                <div
                  key={n._id}
                  style={{
                    padding: 12,
                    borderRadius: 14,
                    background: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.10)",
                  }}
                >
                  <div style={{ opacity: 0.75, fontSize: 12 }}>
                    {n.createdAt ? new Date(n.createdAt).toLocaleString() : ""}
                  </div>
                  <div style={{ marginTop: 6, lineHeight: 1.4 }}>{n.note}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ----------------------------- Profile Modal ----------------------------- */

function UserProfileModal({ open, onClose, token, userId, getProfileUrl }) {
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(null);

  const url = useMemo(() => {
    if (!open || !userId) return null;
    if (getProfileUrl) return getProfileUrl(userId);
    if (!API_BASE_URL) return null;
    return `${API_BASE_URL}/profile/${userId}`;
  }, [open, userId, getProfileUrl]);

  useEffect(() => {
    if (!open || !url) return;

    let alive = true;
    setLoading(true);
    setProfile(null);

    fetchJson(url, { token })
      .then((res) => {
        if (!alive) return;
        setProfile(res?.data || res || null);
      })
      .catch((e) => {
        if (!alive) return;
        toast.error(e.message || "Failed to load profile");
      })
      .finally(() => {
        if (!alive) return;
        setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [open, url, token]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onMouseDown={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        zIndex: 9999,
        display: "grid",
        placeItems: "center",
        padding: 14,
      }}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          width: "min(560px, 100%)",
          background: "#0f172a",
          color: "#fff",
          borderRadius: 16,
          border: "1px solid rgba(255,255,255,0.12)",
          padding: 14,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div style={{ fontWeight: 700 }}>Profile</div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              color: "#fff",
              cursor: "pointer",
              fontSize: 18,
            }}
          >
            ✕
          </button>
        </div>

        <div style={{ marginTop: 12 }}>
          {loading ? (
            <div style={{ opacity: 0.85 }}>Loading...</div>
          ) : !profile ? (
            <div style={{ opacity: 0.75 }}>No profile data</div>
          ) : (
            <div style={{ display: "grid", gap: 10 }}>
              <div style={{ fontSize: 18, fontWeight: 800 }}>
                {profile?.name || "User"}
              </div>
              {profile?.company && (
                <div style={{ opacity: 0.85 }}>{profile.company}</div>
              )}
              {profile?.bio && (
                <div style={{ opacity: 0.95, lineHeight: 1.4 }}>
                  {profile.bio}
                </div>
              )}
              {profile?.experience && (
                <div style={{ opacity: 0.9 }}>{profile.experience}</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ----------------------------- Main wrapper component ----------------------------- */

export default function MeetingRoomFeatures({
  sessionId,
  eventId,
  tableId,
  authToken,
  myUserId,
  myName = "",
  nameByUserId = {},
  enableToasts = true,
  showOverlays = true,
  getProfileUrl,
  children,
}) {
  // ==================== Local networking status state (replaces Redux) ====================
  const [networkingStatusLoading, setNetworkingStatusLoading] = useState(false);
  const [networkingStatusError, setNetworkingStatusError] = useState(null);
  const [networkingStatusResponse, setNetworkingStatusResponse] =
    useState(null);

  // ==================== Local state ====================
  const [socketConnected, setSocketConnected] = useState(() =>
    socketService.isConnected(),
  );
  const [floatingEmojis, setFloatingEmojis] = useState([]);
  const [likeBursts, setLikeBursts] = useState([]);
  const [notesOpen, setNotesOpen] = useState(false);
  const [profileUserId, setProfileUserId] = useState(null);

  const mountedRef = useRef(false);
  const recentSentEmojisRef = useRef([]);
  const prevRosterByTableRef = useRef(new Map());
  const didInitRosterForTableRef = useRef(new Set());

  // ==================== Stable helpers ====================

  const normalizeName = useCallback((v, fallback = "") => {
    const s = String(v ?? "").trim();
    if (!s) return fallback;
    const bad = ["none", "null", "undefined", "nan", "-"];
    if (bad.includes(s.toLowerCase())) return fallback;
    return s;
  }, []);

  const pushFloatingEmoji = useCallback(
    (emoji, fromName) => {
      if (!emoji) return;
      const id = makeId();
      const leftPct = 10 + Math.random() * 80;
      setFloatingEmojis((prev) => [
        ...prev,
        { id, emoji, leftPct, fromName: normalizeName(fromName, "") },
      ]);
      setTimeout(() => {
        if (!mountedRef.current) return;
        setFloatingEmojis((prev) => prev.filter((x) => x.id !== id));
      }, 2600);
    },
    [normalizeName],
  );

  // ==================== Networking status (DIRECT API; no Redux) ====================

  const updateNetworkingStatus = useCallback(
    async ({ networkingRole, networkingIntent } = {}) => {
      if (!sessionId) {
        if (enableToasts) toast.error("Cannot update status: missing session");
        return null;
      }

      if (!networkingRole && !networkingIntent) {
        if (enableToasts)
          toast.error("Cannot update status: role or intent is required");
        return null;
      }

      setNetworkingStatusLoading(true);
      setNetworkingStatusError(null);

      try {
        const result = await updateNetworkingStatusApi(sessionId, {
          networkingRole,
          networkingIntent,
        });

        setNetworkingStatusResponse(result);

        if (enableToasts) toast.success("Networking status updated");
        return result;
      } catch (err) {
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          "Failed to update networking status";

        setNetworkingStatusError(msg);
        if (enableToasts) toast.error(msg);
        return null;
      } finally {
        setNetworkingStatusLoading(false);
      }
    },
    [sessionId, enableToasts],
  );

  // ==================== Socket: mount / connect ====================

  useEffect(() => {
    mountedRef.current = true;
    setSocketConnected(socketService.isConnected());

    const onConnect = () => {
      setSocketConnected(true);
      if (sessionId) {
        socketService.joinSession(sessionId);
        // ✅ Register user in session for personal likes to work
        if (myUserId) {
          socketService.registerSessionUser(myUserId, sessionId);
        }
      }
    };

    const onDisconnect = () => setSocketConnected(false);

    socketService.on("connect", onConnect);
    socketService.on("disconnect", onDisconnect);

    if (socketService.isConnected() && sessionId) {
      socketService.joinSession(sessionId);
      // ✅ Register user in session for personal likes to work
      if (myUserId) {
        socketService.registerSessionUser(myUserId, sessionId);
      }
    }

    return () => {
      mountedRef.current = false;
      socketService.off("connect", onConnect);
      socketService.off("disconnect", onDisconnect);
    };
  }, [sessionId, myUserId]);

  // ==================== Socket: event listeners ====================

  useEffect(() => {
    const handleEmoji = (data) => {
      if (!data?.emoji) return;

      if (
        data?.tableId &&
        tableId &&
        String(data.tableId) !== String(tableId)
      ) {
        return;
      }

      if (
        data?.fromUserId &&
        myUserId &&
        String(data.fromUserId) === String(myUserId)
      ) {
        const now = Date.now();
        const isEcho = recentSentEmojisRef.current.some(
          (x) => x.emoji === data.emoji && now - x.ts < 3000,
        );
        if (isEcho) return;
      }

      const senderLabel =
        data?.fromUserId &&
        myUserId &&
        String(data.fromUserId) === String(myUserId)
          ? "You"
          : normalizeName(data?.fromName) ||
            normalizeName(data?.fromUserName) ||
            normalizeName(data?.userName) ||
            normalizeName(data?.name) ||
            normalizeName(nameByUserId?.[String(data?.fromUserId)]) ||
            "Someone";

      pushFloatingEmoji(data.emoji, senderLabel);
    };

    const handleLike = (data) => {
      if (data?.toUserId && myUserId) {
        if (String(data.toUserId) !== String(myUserId)) return;
      }

      if (
        data?.fromUserId &&
        myUserId &&
        String(data.fromUserId) === String(myUserId)
      ) {
        return;
      }

      const fromName =
        normalizeName(nameByUserId?.[String(data?.fromUserId)]) ||
        normalizeName(data?.fromName) ||
        normalizeName(data?.fromUserName) ||
        "Someone";

      const id = makeId();
      const leftPct = 20 + Math.random() * 60;
      const bottomPct = 15 + Math.random() * 35;

      setLikeBursts((prev) => [
        ...prev,
        { id, fromUserId: data?.fromUserId, fromName, leftPct, bottomPct },
      ]);

      setTimeout(() => {
        if (!mountedRef.current) return;
        setLikeBursts((prev) => prev.filter((x) => x.id !== id));
      }, 2200);

      if (enableToasts) {
        toast(`${fromName} liked you!`, {
          icon: "❤️",
          style: {
            background: "#1a0a0a",
            color: "#fff",
            border: "1px solid rgba(255,100,100,0.5)",
            borderRadius: "12px",
            fontWeight: 600,
          },
          duration: 3500,
        });
      }
    };

    const handleJoined = (payload) => {
      if (!enableToasts) return;

      const clean = (v) => {
        const s = String(v ?? "").trim();
        if (!s) return null;
        const bad = ["none", "null", "undefined", "nan", "-"];
        if (bad.includes(s.toLowerCase())) return null;
        return s;
      };

      const d = payload?.data ?? payload;
      const eventTableId = d?.tableId ? String(d.tableId) : null;

      if (tableId && eventTableId && String(tableId) !== eventTableId) return;

      const eventTableName = clean(d?.tableName);
      const participants = Array.isArray(d?.participants) ? d.participants : [];

      if (!eventTableId || participants.length === 0) {
        const name = clean(d?.name || d?.userName);
        if (!name) return;
        if (d?.userId && myUserId && String(d.userId) === String(myUserId))
          return;
        const where = eventTableName ? ` in ${eventTableName}` : "";
        toast.success(`${name} joined${where}`);
        return;
      }

      const prev = prevRosterByTableRef.current.get(eventTableId) || new Set();
      const next = new Set(
        participants
          .map((p) => p?._id)
          .filter(Boolean)
          .map((x) => String(x)),
      );

      prevRosterByTableRef.current.set(eventTableId, next);

      if (!didInitRosterForTableRef.current.has(eventTableId)) {
        didInitRosterForTableRef.current.add(eventTableId);
        return;
      }

      const joinedIds = [...next].filter((id) => !prev.has(id));
      if (joinedIds.length === 0) return;

      for (const id of joinedIds) {
        if (myUserId && String(myUserId) === id) continue;
        const p = participants.find((x) => String(x?._id) === id);
        const name = clean(p?.name);
        if (!name) continue;
        const roleVal = clean(p?.networkingRole);
        const intentVal = clean(p?.networkingIntent);
        const where = eventTableName ? ` in ${eventTableName}` : "";
        const role = roleVal ? ` (${roleVal})` : "";
        const intent = intentVal ? ` — ${intentVal}` : "";
        toast.success(`${name} joined${where}${role}${intent}`);
      }
    };

    socketService.onSessionEmoji(handleEmoji);
    socketService.onPersonalLike(handleLike);
    socketService.onUserJoinedTable(handleJoined);

    return () => {
      socketService.offSessionEmoji(handleEmoji);
      socketService.offPersonalLike(handleLike);
      socketService.offUserJoinedTable(handleJoined);
    };
  }, [
    enableToasts,
    myUserId,
    tableId,
    pushFloatingEmoji,
    nameByUserId,
    normalizeName,
  ]);

  // ==================== Socket: raw debug interceptor ====================

  useEffect(() => {
    if (!socketService.socket) return;

    const originalOnevent = socketService.socket.onevent;

    socketService.socket.onevent = function (packet) {
      originalOnevent.call(this, packet);
    };

    return () => {
      if (socketService.socket) {
        socketService.socket.onevent = originalOnevent;
      }
    };
  }, []);

  // ==================== Actions ====================

  const sendPersonalLike = useCallback(
    (toUserId, reaction = "like") => {
      if (!sessionId) {
        toast.error("Cannot send like: missing session");
        return false;
      }
      if (!myUserId) {
        toast.error("Cannot send like: you're not identified yet");
        return false;
      }
      if (!toUserId) {
        toast.error("Cannot send like: target user not found");
        return false;
      }
      if (!socketService.isConnected()) {
        toast.error("Cannot send like: not connected");
        return false;
      }

      const payload = {
        sessionId,
        fromUserId: myUserId,
        toUserId: String(toUserId),
        reaction,
      };

      const success = socketService.sendPersonalLike(payload);

      if (success && enableToasts) {
        toast("Like sent! ❤️", {
          icon: "❤️",
          style: {
            background: "#0a1a0a",
            color: "#fff",
            border: "1px solid rgba(100,255,100,0.3)",
            borderRadius: "12px",
          },
          duration: 2000,
        });
      } else if (!success) {
        toast.error("Failed to send like");
      }

      return success;
    },
    [sessionId, myUserId, enableToasts],
  );

  const sendGlobalEmoji = useCallback(
    (emoji) => {
      if (!sessionId || !myUserId || !emoji) return false;

      pushFloatingEmoji(emoji, "You");

      recentSentEmojisRef.current = [
        ...recentSentEmojisRef.current.slice(-10),
        { emoji, ts: Date.now(), fromUserId: myUserId },
      ];

      return socketService.sendSessionEmoji({
        sessionId,
        tableId,
        fromUserId: myUserId,
        fromName: myName || "",
        emoji,
      });
    },
    [sessionId, tableId, myUserId, myName, pushFloatingEmoji],
  );

  const openNotes = useCallback(() => setNotesOpen(true), []);
  const closeNotes = useCallback(() => setNotesOpen(false), []);
  const openProfile = useCallback((userId) => setProfileUserId(userId), []);
  const closeProfile = useCallback(() => setProfileUserId(null), []);

  // ==================== CSS keyframes (once) ====================

  useEffect(() => {
    const styleId = "mrf-keyframes";
    if (document.getElementById(styleId)) return;

    const el = document.createElement("style");
    el.id = styleId;
    el.innerHTML = `
      @keyframes mrf_floatUp {
        0%   { transform: translateY(0)      scale(1);    opacity: 1; }
        80%  { transform: translateY(-180px) scale(1.2);  opacity: 0.8; }
        100% { transform: translateY(-220px) scale(1.35); opacity: 0; }
      }
      @keyframes mrf_heartPulse {
        0%   { transform: translateY(0)      scale(0.5); opacity: 0; }
        15%  { transform: translateY(-10px)  scale(1.4); opacity: 1; }
        40%  { transform: translateY(-25px)  scale(1.2); opacity: 1; }
        70%  { transform: translateY(-70px)  scale(1.0); opacity: 0.8; }
        100% { transform: translateY(-130px) scale(0.7); opacity: 0; }
      }
    `;
    document.head.appendChild(el);
  }, []);

  // ==================== Exposed API ====================

  const api = useMemo(
    () => ({
      socketConnected,

      sendGlobalEmoji,
      sendPersonalLike,

      openNotes,
      closeNotes,

      openProfile,
      closeProfile,

      // Networking status (same names as before)
      updateNetworkingStatus,
      networkingStatusLoading,
      networkingStatusError,
      networkingStatusResponse,

      EmojiQuickBar,
    }),
    [
      socketConnected,
      sendGlobalEmoji,
      sendPersonalLike,
      openNotes,
      closeNotes,
      openProfile,
      closeProfile,
      updateNetworkingStatus,
      networkingStatusLoading,
      networkingStatusError,
      networkingStatusResponse,
    ],
  );

  // ==================== Render ====================

  return (
    <>
      {showOverlays && (
        <>
          <FloatingEmojiOverlay items={floatingEmojis} />
          <LikeOverlay bursts={likeBursts} />
        </>
      )}

      <NotesModal
        open={notesOpen}
        onClose={closeNotes}
        token={authToken}
        sessionId={sessionId}
        eventId={eventId}
        tableId={tableId}
      />

      <UserProfileModal
        open={!!profileUserId}
        onClose={closeProfile}
        token={authToken}
        userId={profileUserId}
        getProfileUrl={getProfileUrl}
      />

      {typeof children === "function" ? children(api) : children || null}
    </>
  );
}
